/**
 * One sign-in for every Teslyn Cloud address under the build's cookie domain (decision 978;
 * docs/specs/cloud/shared-sign-in.md). supabase-js keeps its session through this store: on an `https` address under
 * the cookie domain (production `cloud.teslyn.au`: the front door and `thomson.cloud.teslyn.au`, …) the session is a
 * cookie for that domain (`Domain=cloud.teslyn.au; Path=/; Secure; SameSite=Lax`), so every one of those addresses reads
 * the same sign-in and nothing ever travels in an address. Anywhere else (a company's own domain such as
 * `cloud.thomsonair.com.au`, `localhost`, `*.pages.dev`, cookies blocked) it is this site's localStorage, as before.
 * A cookie is never written for a domain the page is not on.
 *
 * Only the session itself is shared. The PKCE code verifiers (`…-code-verifier` keys) stay in this site's
 * localStorage: a sign-in always comes back to the address that started it.
 *
 * The cookie can be read by the page's own script, like localStorage: the same exposure as before, kept small by the
 * Content Security Policy. It is not an httpOnly cookie (that would need a Teslyn server between the page and
 * Supabase).
 */

/** Chrome's longest lifetime for a cookie (400 days); supabase-js rewrites the cookie at every refresh. */
export const SESSION_COOKIE_MAX_AGE = 400 * 24 * 60 * 60;
/**
 * Characters of value per cookie (after its write id), under the browsers' 4,096 bytes for a cookie's name and value
 * (as @supabase/ssr).
 */
export const COOKIE_CHUNK = 3180;
/** The value's marker: base64url of the UTF-8 JSON supabase-js stores. */
const PREFIX = 'base64-';
/**
 * Every cookie of one write starts `<write id>~`, so chunks from two writes (another address writing at that moment)
 * are never joined into one value: a value whose chunks differ in id is torn and read again.
 */
const ID_SEP = '~';
/** Chunks joined in memory only (a cookie value never holds a line break). */
const JOIN = '\n';
const PROBE = 'teslyn-cookie-probe';

const HOST = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;
const norm = (h: string): string => h.trim().toLowerCase().replace(/^\.+/, '').replace(/\.+$/, '');

/** The page's document, as far as cookies go. */
export interface CookieDoc {
  cookie: string;
}

export type KeyStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * The cookie domain this address may use: the build's setting when this `https` address is that domain or under it,
 * otherwise null (this site's localStorage). An IP address, `localhost`, a single label or a host outside the domain
 * never gets one.
 */
export function cookieDomainFor(
  hostname: string,
  protocol: string,
  configured: string | null | undefined,
): string | null {
  if (!configured || protocol !== 'https:') return null;
  const d = norm(configured);
  const host = norm(hostname);
  if (!HOST.test(d) || !HOST.test(host) || /^[0-9.]+$/.test(host)) return null;
  return host === d || host.endsWith(`.${d}`) ? d : null;
}

/** Whether `host` is the cookie domain or under it. */
export function underDomain(host: string, domain: string | null): boolean {
  if (!domain) return false;
  const h = norm(host);
  const d = norm(domain);
  return h === d || h.endsWith(`.${d}`);
}

function attrs(domain: string, maxAge: number): string {
  return `; Domain=${domain}; Path=/; Max-Age=${maxAge}; Secure; SameSite=Lax`;
}

/** Every cookie the page can see, name to value (the first of a repeated name). */
export function readCookies(doc: CookieDoc): Map<string, string> {
  const out = new Map<string, string>();
  const all = doc.cookie;
  if (!all) return out;
  for (const part of all.split(';')) {
    const i = part.indexOf('=');
    if (i < 0) continue;
    const name = part.slice(0, i).trim();
    if (name && !out.has(name)) out.set(name, part.slice(i + 1).trim());
  }
  return out;
}

/** Whether the browser keeps a cookie for the domain (cookies may be blocked, or the domain refused). */
export function probeCookies(doc: CookieDoc, domain: string): boolean {
  try {
    const v = Math.random().toString(36).slice(2, 10);
    doc.cookie = `${PROBE}=${v}${attrs(domain, 60)}`;
    const ok = readCookies(doc).get(PROBE) === v;
    doc.cookie = `${PROBE}=${attrs(domain, 0)}`;
    return ok;
  } catch {
    return false;
  }
}

function toBase64Url(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(v: string): string | null {
  try {
    const b64 = v.replace(/-/g, '+').replace(/_/g, '/');
    const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}

/** The JSON a stored value holds, or null when it is torn (chunks from two writes) or not ours. */
export function decodeValue(raw: string | null): string | null {
  if (!raw) return null;
  let id: string | null = null;
  let joined = '';
  for (const c of raw.split(JOIN)) {
    const at = c.indexOf(ID_SEP);
    if (at < 1) return null;
    if (id !== null && c.slice(0, at) !== id) return null;
    id = c.slice(0, at);
    joined += c.slice(at + 1);
  }
  if (!joined.startsWith(PREFIX)) return null;
  const text = fromBase64Url(joined.slice(PREFIX.length));
  if (text === null) return null;
  try {
    JSON.parse(text);
    return text;
  } catch {
    return null;
  }
}

/** What the watcher compares: who is signed in and at which level, from the stored session's access token. */
export interface Fingerprint {
  readonly user: string;
  readonly aal: string;
  readonly accessToken: string;
  readonly refreshToken: string;
}

function jwtClaims(token: string): Record<string, unknown> | null {
  const part = token.split('.')[1];
  if (!part) return null;
  const text = fromBase64Url(part);
  if (!text) return null;
  try {
    const v: unknown = JSON.parse(text);
    return v && typeof v === 'object' ? (v as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** The sign-in level of an access token (`aal1` when it does not say). */
export function aalOf(accessToken: string | null | undefined): 'aal1' | 'aal2' {
  return accessToken && jwtClaims(accessToken)?.aal === 'aal2' ? 'aal2' : 'aal1';
}

export function fingerprintOf(json: string | null): Fingerprint | null {
  if (!json) return null;
  try {
    const s = JSON.parse(json) as {
      access_token?: unknown;
      refresh_token?: unknown;
      user?: { id?: unknown };
    };
    if (typeof s.access_token !== 'string' || typeof s.refresh_token !== 'string') return null;
    const user = typeof s.user?.id === 'string' ? s.user.id : String(jwtClaims(s.access_token)?.sub ?? '');
    return { user, aal: aalOf(s.access_token), accessToken: s.access_token, refreshToken: s.refresh_token };
  } catch {
    return null;
  }
}

const wait = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

export interface SharedStoreOptions {
  readonly doc: CookieDoc;
  /** This site's localStorage (the code verifiers, and the session before it moved), or null. */
  readonly local: KeyStore | null;
  /** The cookie domain (from `cookieDomainFor`). */
  readonly domain: string;
  /** supabase-js's storage key for the session (`sb-<project>-auth-token`). */
  readonly sessionKey: string;
  /** Pause between reads of a torn value (another address writing at that moment). */
  readonly retryMs?: number;
}

/**
 * supabase-js's storage on a shared address: the session key in cookies for the domain, split into chunks
 * (`<key>`, or `<key>.0`, `<key>.1`, … when longer than one cookie holds), every other key in this site's
 * localStorage.
 */
export class SharedSessionStore {
  readonly domain: string;
  readonly sessionKey: string;
  /** The stored value as this page last wrote or noticed it (the watcher's reference); set when the page starts. */
  lastSeen: string | null = null;
  private readonly doc: CookieDoc;
  private readonly local: KeyStore | null;
  private readonly retryMs: number;

  constructor(o: SharedStoreOptions) {
    this.doc = o.doc;
    this.local = o.local;
    this.domain = o.domain;
    this.sessionKey = o.sessionKey;
    this.retryMs = o.retryMs ?? 30;
  }

  /** The names of the session's cookies now set (the whole key and every chunk). */
  private names(): string[] {
    const k = this.sessionKey;
    return [...readCookies(this.doc).keys()].filter(
      (n) => n === k || (n.startsWith(`${k}.`) && /^\d+$/.test(n.slice(k.length + 1))),
    );
  }

  /** The stored cookies' values as written (in chunk order), without decoding; null when there is none. */
  readRaw(): string | null {
    const jar = readCookies(this.doc);
    const whole = jar.get(this.sessionKey);
    if (whole) return whole;
    const parts: string[] = [];
    for (let i = 0; ; i += 1) {
      const c = jar.get(`${this.sessionKey}.${i}`);
      if (c === undefined || c === '') break;
      parts.push(c);
    }
    return parts.length ? parts.join(JOIN) : null;
  }

  async getItem(key: string): Promise<string | null> {
    if (key !== this.sessionKey) return this.local?.getItem(key) ?? null;
    // a value read while another address writes its chunks is torn; it settles within a moment
    for (let attempt = 0; attempt < 4; attempt += 1) {
      // `lastSeen` stays as it was: reading is not noticing, the watcher compares against what this page last saw
      const raw = this.readRaw();
      if (raw === null) return null;
      const json = decodeValue(raw);
      if (json !== null) return json;
      await wait(this.retryMs);
    }
    return null;
  }

  setItem(key: string, value: string): void {
    if (key !== this.sessionKey) {
      this.local?.setItem(key, value);
      return;
    }
    const data = PREFIX + toBase64Url(value);
    const id = Math.random().toString(36).slice(2, 10) || 'w';
    const chunks: string[] = [];
    for (let i = 0; i < data.length; i += COOKIE_CHUNK)
      chunks.push(`${id}${ID_SEP}${data.slice(i, i + COOKIE_CHUNK)}`);
    const before = this.names();
    // the new cookies first, then the old ones go, so a reader on another address sees one whole value or the other
    const written = chunks.length === 1 ? [this.sessionKey] : chunks.map((_, i) => `${this.sessionKey}.${i}`);
    written.forEach((name, i) => {
      this.doc.cookie = `${name}=${chunks[i]}${attrs(this.domain, SESSION_COOKIE_MAX_AGE)}`;
    });
    for (const name of before)
      if (!written.includes(name)) this.doc.cookie = `${name}=${attrs(this.domain, 0)}`;
    this.lastSeen = chunks.join(JOIN);
    // a copy left in this site's storage from before the move is never read again
    this.local?.removeItem(key);
  }

  removeItem(key: string): void {
    if (key !== this.sessionKey) {
      this.local?.removeItem(key);
      return;
    }
    for (const name of this.names()) this.doc.cookie = `${name}=${attrs(this.domain, 0)}`;
    this.local?.removeItem(key);
    this.lastSeen = null;
  }
}

export type MigrationResult = 'none' | 'moved' | 'dropped';

/**
 * Once, on the first visit after the change: a session this site kept in localStorage moves into the shared cookie
 * when there is none; when another address has already put one there, the shared one wins and the old copy goes.
 */
export function migrateLocalSession(store: SharedSessionStore, local: KeyStore | null): MigrationResult {
  let old: string | null = null;
  try {
    old = local?.getItem(store.sessionKey) ?? null;
  } catch {
    return 'none';
  }
  if (!old) return 'none';
  const shared = decodeValue(store.readRaw());
  if (shared === null && fingerprintOf(old)) {
    store.setItem(store.sessionKey, old);
    return 'moved';
  }
  local?.removeItem(store.sessionKey);
  return 'dropped';
}

/** What the watcher needs of the Supabase client. */
export interface WatchedAuth {
  readonly auth: {
    signOut(o: { scope: 'local' }): Promise<unknown>;
    setSession(s: { access_token: string; refresh_token: string }): Promise<unknown>;
  };
}

export type SharedChange = 'none' | 'signed-out' | 'signed-in' | 'person' | 'level' | 'tokens' | 'torn';

/**
 * One look at the shared cookie: when another address changed it, tell this page's client. Signed out there: this
 * page signs out too (no network call, the session is already gone). Signed in there, as another person, or at
 * another level (two-step sign-in done there): this page takes the session, so its screens follow. New tokens of the
 * same sign-in (a refresh there) need nothing: supabase-js reads the cookie whenever it needs the session.
 */
export async function checkShared(store: SharedSessionStore, sb: WatchedAuth): Promise<SharedChange> {
  const raw = store.readRaw();
  if (raw === store.lastSeen) return 'none';
  const nowJson = raw === null ? null : decodeValue(raw);
  if (raw !== null && nowJson === null) return 'torn'; // another address is writing; look again next time
  const before = fingerprintOf(decodeValue(store.lastSeen));
  const now = fingerprintOf(nowJson);
  store.lastSeen = raw;
  if (!now) {
    if (!before) return 'none';
    await sb.auth.signOut({ scope: 'local' }).catch(() => undefined);
    return 'signed-out';
  }
  const change: SharedChange = !before
    ? 'signed-in'
    : before.user !== now.user
      ? 'person'
      : before.aal !== now.aal
        ? 'level'
        : 'tokens';
  if (change !== 'tokens')
    await sb.auth
      .setSession({ access_token: now.accessToken, refresh_token: now.refreshToken })
      .catch(() => undefined);
  return change;
}

/** How often an open, visible page looks at the shared cookie. */
export const WATCH_MS = 2000;

/**
 * Keeps this page in step with the other addresses: looks at the cookie every `WATCH_MS` while the page is visible,
 * and at once when it becomes visible or gets focus. Returns the function that stops it.
 */
export function watchShared(
  store: SharedSessionStore,
  sb: WatchedAuth,
  win: Pick<Window, 'addEventListener' | 'removeEventListener' | 'setInterval' | 'clearInterval'> & {
    readonly document: { readonly visibilityState: string };
  } = window,
): () => void {
  let busy = false;
  const look = (): void => {
    if (busy || win.document.visibilityState === 'hidden') return;
    busy = true;
    void checkShared(store, sb).finally(() => {
      busy = false;
    });
  };
  const timer = win.setInterval(look, WATCH_MS);
  win.addEventListener('visibilitychange', look);
  win.addEventListener('focus', look);
  return () => {
    win.clearInterval(timer);
    win.removeEventListener('visibilitychange', look);
    win.removeEventListener('focus', look);
  };
}
